/**
 * Posting Limits and Feature Tiers Configuration
 * 
 * Defines what's available in free vs paid tiers for:
 * - Scrimmage postings
 * - Saved searches
 * - Alert frequency
 * - Dispatches
 * - Portal features
 */

// ============================================================================
// TIER DEFINITIONS
// ============================================================================

export const TIERS = {
  FREE: 'free',
  PRO: 'paid'
}

// ============================================================================
// SCRIMMAGE LIMITS
// ============================================================================

export const SCRIMMAGE_LIMITS = {
  free: {
    activePostings: 3,        // 3 active scrimmage postings
    savedSearches: 2,         // 2 saved searches
    alertFrequency: ['weekly'], // Weekly digest only - no daily or immediate
    matchRadius: 100          // Miles for matching
  },
  paid: {
    activePostings: Infinity, // Unlimited postings
    savedSearches: Infinity,  // Unlimited saved searches
    alertFrequency: ['immediate', 'daily', 'weekly'], // Includes immediate and daily
    matchRadius: Infinity,    // Unlimited radius
    priorityPlacement: true,  // Postings shown first
    calendarSync: true        // Auto-sync calendar (not just upload)
  }
}

// ============================================================================
// DISPATCH LIMITS
// ============================================================================

export const DISPATCH_LIMITS = {
  free: {
    dispatchesPerMonth: 2,    // 2 dispatches per month
    maxParents: 25,           // 25 parent emails
    wikiAutoExtract: false,
    emailCustomization: false
  },
  paid: {
    dispatchesPerMonth: Infinity,
    maxParents: Infinity,
    wikiAutoExtract: true,
    emailCustomization: true
  }
}

// ============================================================================
// PORTAL LIMITS
// ============================================================================

export const PORTAL_LIMITS = {
  free: {
    customSections: 2,        // 2 custom sections
    fileStorage: '0',         // No file storage for free tier
    galleryItems: 0,          // No gallery items for free tier
    customDomain: false,
    canEditWiki: false,       // Can only add, not edit/delete
    canPinWiki: false,        // Cannot pin wiki items
    canUseTasks: false,       // No task management
    canUseRoster: false,      // No roster management
    canUseSnacks: false       // No snack schedule
  },
  paid: {
    customSections: Infinity,
    fileStorage: '5GB',
    galleryItems: Infinity,
    customDomain: true,
    canEditWiki: true,        // Full wiki CRUD
    canPinWiki: true,         // Can pin wiki items
    canUseTasks: true,        // Task management
    canUseRoster: true,       // Roster management
    canUseSnacks: true        // Snack schedule
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// Legacy exports for backwards compatibility
export const POSTING_LIMITS = {
  free: SCRIMMAGE_LIMITS.free.activePostings,
  pro: SCRIMMAGE_LIMITS.paid.activePostings
}

export function getPostingLimit(seasonTier) {
  return seasonTier === 'paid' 
    ? SCRIMMAGE_LIMITS.paid.activePostings 
    : SCRIMMAGE_LIMITS.free.activePostings
}

export function canCreatePosting(activeCount, seasonTier) {
  const limit = getPostingLimit(seasonTier)
  return activeCount < limit
}

export function getPostingLimitMessage(activeCount, seasonTier) {
  const limit = getPostingLimit(seasonTier)
  if (seasonTier === 'paid') {
    return null // No limit message for Pro
  }
  return `${activeCount}/${limit} postings • Upgrade for unlimited`
}

// New helper functions
export function getSavedSearchLimit(seasonTier) {
  return seasonTier === 'paid'
    ? SCRIMMAGE_LIMITS.paid.savedSearches
    : SCRIMMAGE_LIMITS.free.savedSearches
}

export function canCreateSavedSearch(currentCount, seasonTier) {
  const limit = getSavedSearchLimit(seasonTier)
  return currentCount < limit
}

export function getAllowedAlertFrequencies(seasonTier) {
  return seasonTier === 'paid'
    ? SCRIMMAGE_LIMITS.paid.alertFrequency
    : SCRIMMAGE_LIMITS.free.alertFrequency
}

export function canUseInstantAlerts(seasonTier) {
  return seasonTier === 'paid'
}

export function getDispatchLimit(seasonTier) {
  return seasonTier === 'paid'
    ? DISPATCH_LIMITS.paid.dispatchesPerMonth
    : DISPATCH_LIMITS.free.dispatchesPerMonth
}

export function canSendDispatch(dispatchesSentThisMonth, seasonTier) {
  const limit = getDispatchLimit(seasonTier)
  return dispatchesSentThisMonth < limit
}

export function getParentLimit(seasonTier) {
  return seasonTier === 'paid'
    ? DISPATCH_LIMITS.paid.maxParents
    : DISPATCH_LIMITS.free.maxParents
}

export function hasWikiAutoExtract(seasonTier) {
  return seasonTier === 'paid'
}

export function hasPriorityPlacement(seasonTier) {
  return seasonTier === 'paid'
}

export function hasCalendarSync(seasonTier) {
  return seasonTier === 'paid'
}

// New helper functions for wiki, tasks, roster, snacks
export function canEditWiki(seasonTier) {
  return seasonTier === 'paid'
}export function canDeleteWiki(seasonTier) {
  return seasonTier === 'paid'
}

export function canPinWiki(seasonTier) {
  return seasonTier === 'paid'
}

export function canUseTasks(seasonTier) {
  return seasonTier === 'paid'
}

export function canUseRoster(seasonTier) {
  return seasonTier === 'paid'
}

export function canUseSnacks(seasonTier) {
  return seasonTier === 'paid'
}

export function canUseFiles(seasonTier) {
  return seasonTier === 'paid'
}

export function getCustomSectionsLimit(seasonTier) {
  return seasonTier === 'paid'
    ? PORTAL_LIMITS.paid.customSections
    : PORTAL_LIMITS.free.customSections
}

// ============================================================================
// FEATURE COMPARISON (for pricing page)
// ============================================================================

export const FEATURE_COMPARISON = {
  scrimmage: [
    {
      feature: 'Browse all scrimmages',
      free: true,
      paid: true,
      description: 'Search and filter scrimmage opportunities'
    },
    {
      feature: 'Calendar upload for matching',
      free: true,
      paid: true,
      description: 'Upload your calendar to find conflicts'
    },
    {
      feature: 'Scrimmage postings',
      free: '3 active',
      paid: 'Unlimited',
      description: 'Post your own scrimmage opportunities'
    },
    {
      feature: 'Saved searches',
      free: '2',
      paid: 'Unlimited',
      description: 'Save your search filters'
    },
    {
      feature: 'Email alerts',
      free: 'Weekly digest',
      paid: 'Immediate + daily + weekly',
      description: 'Get notified about new matches'
    },
    {
      feature: 'Priority placement',
      free: false,
      paid: true,
      description: 'Your postings appear first in search results'
    },
    {
      feature: 'Calendar auto-sync',
      free: false,
      paid: true,
      description: 'Automatically sync with your team calendar'
    }
  ],
  dispatch: [
    {
      feature: 'Weekly dispatches',
      free: '2/month',
      paid: 'Unlimited',
      description: 'Send formatted emails to parents'
    },
    {
      feature: 'Parent recipients',
      free: '25 parents',
      paid: 'Unlimited',
      description: 'Number of parents you can email'
    },
    {
      feature: 'AI wiki extraction',
      free: false,
      paid: true,
      description: 'Auto-update wiki from dispatches'
    }
  ],
  portal: [
    {
      feature: 'Team portal',
      free: true,
      paid: true,
      description: 'Shareable team page for parents'
    },
    {
      feature: 'Custom sections',
      free: '2',
      paid: 'Unlimited',
      description: 'Add custom content sections'
    },
    {
      feature: 'File storage',
      free: 'None',
      paid: '5GB',
      description: 'Upload files for parents'
    },
    {
      feature: 'Wiki management',
      free: 'Add only',
      paid: 'Full CRUD + pin',
      description: 'Manage team wiki items'
    },
    {
      feature: 'Team roster',
      free: false,
      paid: true,
      description: 'Manage player roster'
    },
    {
      feature: 'Snack schedule',
      free: false,
      paid: true,
      description: 'Assign snack duties'
    },
    {
      feature: 'Task management',
      free: false,
      paid: true,
      description: 'Track team tasks'
    }
  ]
}// Pricing information
export const PRICING = {
  free: {
    name: 'Free',
    price: 0,
    period: 'forever',
    description: 'Get started with basic team tools'
  },
  paid: {
    name: 'Pro',
    price: 49,
    period: 'season',
    description: 'Everything you need to manage your team'
  }
}
